import React, { FC, MouseEvent, useCallback, useMemo } from 'react';

import { CellContext } from '@tanstack/react-table';

import { track } from '@adsk/acs-web-analytics';
import { EntitlementsProject, useAlloyFeatureFlag } from '@adsk/alloy-provider';
import { Flex } from '@adsk/alloy-react-box';

import { ProductsAndToolsSelectionMenu } from './ProductsAndToolsSelectionMenu';
import { ProductsListContainer } from './ProductsListContainer';
import { AnalyticsEvents, FEATURE_FLAGS } from '../../../constants';
import {
  hasToolKey,
  parseProductAndToolKey,
} from '../../../utils/defaultProductAndToolHelpers';
import { getSelectedProduct } from '../../../utils/getSelectedProduct';
import { useProductEntryFlowContext } from '../../Contexts/ProductEntryFlowContext';

export const ProductEntryFlowCell: FC<
  CellContext<EntitlementsProject, EntitlementsProject['products']>
> = ({ row }) => {
  const { value: toolEntryFlow } = useAlloyFeatureFlag(
    FEATURE_FLAGS.ToolEntryFlow,
  );
  const { loading, selectedProductAndToolIds, storeProductSelection } =
    useProductEntryFlowContext();
  const project = row.original;
  let selectedProductId = selectedProductAndToolIds?.[project.id];
  if (!toolEntryFlow && selectedProductId && hasToolKey(selectedProductId)) {
    // In case we opened the FF and after some time closed it, ignore the saved tool ID.
    selectedProductId = parseProductAndToolKey(selectedProductId)?.productId;
  }
  const currentProduct = useMemo(
    () => getSelectedProduct(project, selectedProductId),
    [project, selectedProductId],
  );

  const disableRowClick = useCallback((event: MouseEvent) => {
    event.stopPropagation();
    event.preventDefault();
  }, []);

  const onProductClick = useCallback(
    async (_, { id, name }) => {
      track(AnalyticsEvents.productEntryFlowNavigate, {
        project_name: project.name,
        product_id: id,
      });

      storeProductSelection({
        project,
        productId: id,
        productName: name,
      });
    },
    [project, storeProductSelection],
  );

  if (row.original.platform !== 'acc' || (!toolEntryFlow && !currentProduct)) {
    return null;
  }

  return (
    <Flex
      data-testid={`product-entry-${row.original.id}`}
      onClick={disableRowClick}
      height="100%"
    >
      {toolEntryFlow ? (
        <ProductsAndToolsSelectionMenu project={project} />
      ) : (
        <ProductsListContainer
          disabled={loading}
          project={project}
          currentProduct={currentProduct}
          onProductClick={onProductClick}
        />
      )}
    </Flex>
  );
};
